/**
 * §6.3's Updated column: *"Relative (`2 days ago`), absolute in tooltip."*
 *
 * Both strings come from `Intl`, driven by `i18n.language`, rather than from the catalogue — and that
 * is not an exception to §11's "every string through i18n". A relative time needs plural rules and
 * unit names per locale, and a date needs an ordering, a separator and a month name; the platform's
 * locale data carries all of it, while a hand-written `"{{count}} days ago"` key would carry English
 * grammar into every language added later. There is **no English literal in this module** — which is
 * the property §11 is actually protecting.
 *
 * The API sends `updated_at` as an ISO-8601 instant; nothing here computes a duration the payload
 * already states, and nothing here is a derived value in `CONTEXT.md`'s sense.
 */

/** Largest unit first: a two-day-old row reads "2 days ago", never "48 hours ago". */
const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['week', 7 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
];

/**
 * `2026-07-27T10:00:00Z` → `2 days ago`, and `-1 day` → `yesterday` (`numeric: 'auto'`, which is
 * what makes the column read like prose instead of like a log).
 *
 * `now` is a parameter so the unit tests are not clock-dependent; the screen leaves it defaulted.
 */
export function relativeUpdatedAt(iso: string, locale: string, now: number = Date.now()): string {
  const instant = Date.parse(iso);
  if (Number.isNaN(instant)) return '';

  const elapsed = instant - now;
  const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  for (const [unit, milliseconds] of UNITS) {
    if (Math.abs(elapsed) >= milliseconds) {
      return format.format(Math.round(elapsed / milliseconds), unit);
    }
  }

  // Under a minute. `numeric: 'auto'` renders this as "now" rather than "in 0 seconds".
  return format.format(0, 'second');
}

/**
 * The absolute instant the tooltip carries — date **and** time, in the reader's own zone.
 *
 * A relative time alone cannot answer *"was that before or after the interview"*, which is the
 * question this column is read for; the tooltip is where §6.3 puts the answer.
 */
export function absoluteUpdatedAt(iso: string, locale: string): string {
  const instant = Date.parse(iso);
  if (Number.isNaN(instant)) return '';

  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    instant,
  );
}
